import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, Wifi, WifiOff, Clock, AlertTriangle, CheckCircle } from 'lucide-react';

const Heartbeat = () => {
  const [heartbeatData, setHeartbeatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchHeartbeatStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/heartbeat-status');
      setHeartbeatData(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching heartbeat status:', err);
      setError('Failed to fetch heartbeat status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHeartbeatStatus();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchHeartbeatStatus, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const formatUptime = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatLastHeartbeat = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hours ago`;
    return date.toLocaleDateString();
  };

  const getStatusIcon = (isOnline) => {
    return isOnline ? (
      <Wifi className="w-5 h-5 text-success-400" />
    ) : (
      <WifiOff className="w-5 h-5 text-danger-400" />
    );
  };

  if (loading && !heartbeatData) {
    return (
      <div className="flex items-center justify-center h-full">
        <Activity className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="material-regular border-danger-500/30 rounded-2xl p-5">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-danger-400" />
            <span className="text-danger-300 text-subheadline">{error}</span>
          </div>
          <button
            onClick={fetchHeartbeatStatus}
            className="btn btn-danger mt-4"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="dashboard-header">Heartbeat Monitor</h1>
            <p className="text-secondary mt-2">Real-time locker connectivity status and uptime monitoring</p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={fetchHeartbeatStatus}
              disabled={loading}
              className="btn btn-tinted"
            >
              <Activity className="w-4 h-4 mr-2" />
              <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
            </button>
            {lastUpdate && (
              <div className="text-footnote text-muted">
                Last updated: {lastUpdate.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {heartbeatData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="metric-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="metric-label">Total Lockers</p>
                  <p className="metric-value">{heartbeatData.total}</p>
                </div>
                <div className="w-12 h-12 bg-primary-500/15 rounded-xl flex items-center justify-center">
                  <Activity className="w-6 h-6 text-primary-400" />
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="metric-label">Online</p>
                  <p className="metric-value text-success-400">{heartbeatData.online}</p>
                </div>
                <div className="w-12 h-12 bg-success-500/15 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-success-400" />
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="metric-label">Offline</p>
                  <p className="metric-value text-danger-400">{heartbeatData.offline}</p>
                </div>
                <div className="w-12 h-12 bg-danger-500/15 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-danger-400" />
                </div>
              </div>
            </div>

            <div className="metric-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="metric-label">Avg Uptime</p>
                  <p className="metric-value">{formatUptime(heartbeatData.averageUptime)}</p>
                </div>
                <div className="w-12 h-12 bg-warning-500/15 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-warning-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Online Lockers */}
          <div className="dashboard-card mb-6 !p-0 overflow-hidden">
            <div className="px-6 py-4 border-b border-white/[0.07]">
              <h2 className="text-title3 text-white flex items-center space-x-2">
                <Wifi className="w-5 h-5 text-success-400" />
                <span>Online Lockers ({heartbeatData.lockersByStatus.online.length})</span>
              </h2>
            </div>
            <div className="p-6">
              {heartbeatData.lockersByStatus.online.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {heartbeatData.lockersByStatus.online.map((locker) => (
                    <div key={locker.id} className="material-thin border-success-500/25 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-headline text-white">{locker.name}</h3>
                        {getStatusIcon(true)}
                      </div>
                      <div className="space-y-1 text-footnote">
                        <div className="flex justify-between">
                          <span className="text-secondary">Uptime:</span>
                          <span className="text-success-400 font-medium">{formatUptime(locker.uptime)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-secondary">Last Heartbeat:</span>
                          <span className="text-white font-medium">{formatLastHeartbeat(locker.lastHeartbeat)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted text-center py-4">No online lockers</p>
              )}
            </div>
          </div>

          {/* Offline Lockers */}
          <div className="dashboard-card mb-6 !p-0 overflow-hidden">
            <div className="px-6 py-4 border-b border-white/[0.07]">
              <h2 className="text-title3 text-white flex items-center space-x-2">
                <WifiOff className="w-5 h-5 text-danger-400" />
                <span>Offline Lockers ({heartbeatData.lockersByStatus.offline.length})</span>
              </h2>
            </div>
            <div className="p-6">
              {heartbeatData.lockersByStatus.offline.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {heartbeatData.lockersByStatus.offline.map((locker) => (
                    <div key={locker.id} className="material-thin border-danger-500/25 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-headline text-white">{locker.name}</h3>
                        {getStatusIcon(false)}
                      </div>
                      <div className="space-y-1 text-footnote">
                        <div className="flex justify-between">
                          <span className="text-secondary">Last Uptime:</span>
                          <span className="text-danger-400 font-medium">{formatUptime(locker.uptime)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-secondary">Last Heartbeat:</span>
                          <span className="text-white font-medium">{formatLastHeartbeat(locker.lastHeartbeat)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted text-center py-4">No offline lockers</p>
              )}
            </div>
          </div>

          {/* No Heartbeat Lockers */}
          {heartbeatData.lockersByStatus.noHeartbeat.length > 0 && (
            <div className="dashboard-card !p-0 overflow-hidden">
              <div className="px-6 py-4 border-b border-white/[0.07]">
                <h2 className="text-title3 text-white flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-warning-400" />
                  <span>No Heartbeat ({heartbeatData.lockersByStatus.noHeartbeat.length})</span>
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {heartbeatData.lockersByStatus.noHeartbeat.map((locker) => (
                    <div key={locker.id} className="material-thin border-warning-500/25 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-headline text-white">{locker.name}</h3>
                        <AlertTriangle className="w-5 h-5 text-warning-400" />
                      </div>
                      <p className="text-footnote text-secondary">No heartbeat received</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Heartbeat; 